<template>
  <div class="faceted-search">
    <div
      class="title-2"
      v-text="$t(`careProvider.search.title.${$props.avmNetwork}`)"
    />

    <div v-if="!isCriteriaHidden">
      <basic-location
        v-model="formData.location"
        name="location"
        :contract-start-date="$props.contractStartDate"
        :label="$t('person.plz')"
        :supporting-text="$t('location.placeholder')"
      />

      <basic-text-input
        v-model="formData.name"
        name="name"
        :label="$t('careProvider.search.name')"
      />

      <basic-select
        v-model="formData.discountGroups"
        name="discountGroups"
        multi-select
        :label="$t('careProvider.discount')"
        :options="discountGroupOptions"
      />

      <basic-checkbox
        v-model="formData.admitPatients"
        name="admitPatients"
        :label="$t('careProvider.search.admitPatients')"
      />
    </div>

    <basic-button
      class="faceted-search__toggle"
      size="sm"
      hierarchy="secondary"
      :label="criteriaToggleLabel"
      @click="isCriteriaHidden = !isCriteriaHidden"
    >
      <template #trailing>
        <the-icon
          art="solid"
          :name="isCriteriaHidden ? 'chevron-down' : 'chevron-up'"
        />
      </template>
    </basic-button>

    <div
      v-tooltip="$t('careProvider.search.tooltip')"
      class="faceted-search__tooltip"
      html-tooltip
    >
      <span
        class="caption-bold"
        v-text="$t('product.reduction')"
      />
      <the-icon
        art="regular"
        name="circle-info"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from 'vue'

  import { debounce } from 'lodash'

  import useI18n from '@shared/hooks/useI18n'

  import { BasicButton, BasicCheckbox, BasicLocation, BasicSelect, BasicTextInput } from '@shared/components/Basic'
  import { DISCOUNT_GROUPS } from '@shared/components/CareProvider/constants'

  // HOOKS
  const { t } = useI18n()

  const emit = defineEmits(['filter', 'search'])
  const props = defineProps({
    admitPatients: {
      type: Boolean,
      default: false,
    },

    avmNetwork: {
      type: String,
      default: ''
    },

    contractStartDate: {
      type: String,
      required: true,
    },

    discountGroups: {
      type: Array,
      required: true,
    },

    location: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const SEARCH_DEBOUNCE = 2000

  const discountGroupOptions = Object.values(DISCOUNT_GROUPS)
    .sort((a,b) => a - b)
    .map(groupName => {
      return {
        value: groupName,
        label: t(`careProvider.discountGroup.${groupName}.text`)
      }
    })

  const formData = ref({
    admitPatients: props.admitPatients,
    discountGroups: props.discountGroups,
    location: props.location,
    name: props.name,
  })
  const isCriteriaHidden = ref(false)

  // COMPUTED
  const criteriaToggleLabel = computed(() => {
    const prefix = 'medicalofficesearch.filters.toggle'
    return isCriteriaHidden.value ? t(`${prefix}.show`) : t(`${prefix}.hide`)
  })

  // METHODS
  const debouncedSearch = debounce(emitSearch, SEARCH_DEBOUNCE)

  function emitFilter(discountGroups) {
    emit('filter', {
      discountGroups,
    })
  }

  function emitSearch({ admitPatients, location, name }) {
    emit('search', {
      admitPatients: admitPatients || formData.value.admitPatients,
      location: location || formData.value.location,
      name: name || formData.value.name,
    })
  }

  // WATCHER
  watch(
    () => formData.value.location,
    (val) => {
      if (val) emitSearch({ location: val })
    }

  )

  watch(
    () => formData.value.name,
    (val) => debouncedSearch({ name: val })
  )

  watch(
    () => formData.value.discountGroups,
    (val) => emitFilter(val)
  )

  watch(
    () => formData.value.admitPatients,
    (val) => emitSearch({ admitPatients: val })
  )
</script>

<style scoped>
.faceted-search {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-04);
  padding: 0 0 var(--fixed-spacing-fix-02);
  border-bottom: 1px solid var(--on-surface);
  margin: var(--fixed-spacing-fix-19) 0 0;
}

.faceted-search__toggle {
  width: fit-content;
}

.faceted-search__tooltip {
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-02);
  align-self: flex-end;
  cursor: pointer;
}

.faceted-search__tooltip .icon {
  color: var(--primary);
}

@media (--v-large) {
  .faceted-search {
    margin: var(--fixed-spacing-fix-08) 0 0;
  }
}
</style>
